import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getAdminUsers, adminUnlock, adminRevoke, adminSetActive, adminCreateUser, adminSetFreeCredits, adminDeleteUser, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Eye, Users, CheckCircle2, XCircle, ArrowLeft, LogOut, Loader2, ShieldCheck,
  Unlock, Ban, UserCheck, UserX, UserPlus, Gift, AlertTriangle, Flame, Trash2,
} from "lucide-react";

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${tone}`}>
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div>
        <p className="text-2xl font-extrabold tracking-tight text-slate-900">{value}</p>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</p>
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [creating, setCreating] = useState(false);
  // Account in attesa di conferma di eliminazione, e l'email ridigitata dall'admin.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState("");

  const load = () => {
    setLoading(true);
    getAdminUsers()
      .then(setData)
      .catch(() => toast.error("Impossibile caricare gli utenti."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const doLogout = async () => { navigate("/"); await logout(); };

  const runAction = async (userId, fn, successMsg) => {
    setBusyId(userId);
    try {
      await fn();
      toast.success(successMsg);
      const fresh = await getAdminUsers();
      setData(fresh);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail ? formatApiErrorDetail(detail) : "Operazione non riuscita.");
    } finally {
      setBusyId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await adminCreateUser(form);
      toast.success(`Utente ${form.email} creato con licenza gratuita.`);
      setForm({ email: "", password: "", name: "" });
      setShowCreate(false);
      const fresh = await getAdminUsers();
      setData(fresh);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Creazione non riuscita.";
      toast.error(typeof msg === "string" ? msg : "Creazione non riuscita.");
    } finally {
      setCreating(false);
    }
  };

  const promptFreeCredits = (u) => {
    const raw = window.prompt(
      `Referti gratuiti per ${u.email}\nUsati: ${u.free_reports_used ?? 0}. Nuovo limite:`,
      String(u.free_report_limit ?? 1),
    );
    if (raw === null) return;
    const limit = Number.parseInt(raw, 10);
    if (!Number.isInteger(limit) || limit < 0) { toast.error("Inserisci un numero intero non negativo."); return; }
    runAction(u.user_id, () => adminSetFreeCredits(u.user_id, limit), `Limite referti gratuiti impostato a ${limit}.`);
  };

  // Stesso confronto che rifà il server prima di cancellare: normalizzato, mai sensibile al caso.
  const norm = (s) => (s || "").trim().toLowerCase();
  const canDelete = !!deleteTarget && norm(confirmEmail) === norm(deleteTarget.email);

  const openDelete = (u) => { setConfirmEmail(""); setDeleteTarget(u); };
  const closeDelete = () => { setDeleteTarget(null); setConfirmEmail(""); };

  const confirmDelete = () => {
    if (!canDelete) return;
    const { user_id: id, email } = deleteTarget;
    closeDelete();
    runAction(id, () => adminDeleteUser(id, email), `Account ${email} eliminato definitivamente.`);
  };

  const users = data?.users || [];
  const paid = users.filter((u) => u.unlocked && u.role !== "admin").length;
  const configured = users.filter((u) => u.configured).length;
  // Il segnale di vendita più utile del pannello: chi ha esaurito il gratuito e non ha pagato.
  const warmLeads = users.filter((u) => !u.unlocked && u.role !== "admin" && (u.free_reports_used ?? 0) > 0).length;
  const abuseAlerts = data?.abuse_alerts || [];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-md bg-primary text-white flex items-center justify-center">
              <Eye className="w-5 h-5" strokeWidth={2} />
            </span>
            <span className="leading-tight">
              <span className="block text-base font-bold tracking-tight">VisioScreening</span>
              <span className="block text-[11px] font-medium uppercase tracking-widest text-slate-400">Pannello Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/app")} data-testid="admin-goto-app">
              <ArrowLeft className="w-4 h-4" /> App
            </Button>
            <Button variant="ghost" onClick={doLogout} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-md bg-primary/10 text-primary flex items-center justify-center">
            <ShieldCheck className="w-6 h-6" strokeWidth={1.8} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-extrabold tracking-tight">Utenti registrati</h1>
            <p className="text-slate-500 text-sm">Gestisci licenze e stato degli account.</p>
          </div>
          <Button onClick={() => setShowCreate((v) => !v)} data-testid="toggle-create-user-btn">
            <UserPlus className="w-4 h-4" /> Crea utente gratuito
          </Button>
        </div>

        {showCreate && (
          <form onSubmit={handleCreate} data-testid="create-user-form"
            className="rounded-xl border border-slate-200 bg-white p-5 mb-6 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div className="sm:col-span-1">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Email</label>
              <Input type="email" required value={form.email} data-testid="create-user-email"
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="clinica@esempio.it" />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Password</label>
              <Input type="text" required minLength={6} value={form.password} data-testid="create-user-password"
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min. 6 caratteri" />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Nome (opzionale)</label>
              <Input type="text" value={form.name} data-testid="create-user-name"
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome referente" />
            </div>
            <div className="sm:col-span-1">
              <Button type="submit" disabled={creating} className="w-full" data-testid="submit-create-user">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Crea e sblocca
              </Button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-20 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Caricamento…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
              <Stat icon={Users} label="Account totali" value={data?.total || 0} tone="bg-slate-100 text-slate-700" />
              <Stat icon={CheckCircle2} label="Licenze attive" value={paid} tone="bg-emerald-100 text-emerald-700" />
              <Stat icon={ShieldCheck} label="Configurati" value={configured} tone="bg-primary/10 text-primary" />
              <Stat icon={Flame} label="Gratuito esaurito" value={warmLeads} tone="bg-amber-100 text-amber-700" />
            </div>

            {abuseAlerts.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-8" data-testid="abuse-alerts">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                  <AlertTriangle className="w-4 h-4" /> Referti gratuiti concentrati sullo stesso IP (ultimi 7 giorni)
                </div>
                <ul className="mt-2 space-y-1 text-xs text-amber-800">
                  {abuseAlerts.map((a) => (
                    <li key={a.ip_hash}>
                      <code className="font-mono">{a.ip_hash.slice(0, 12)}…</code> — {a.reports} referti su {a.accounts} account
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-users-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Struttura</th>
                      <th className="px-4 py-3">Licenza</th>
                      <th className="px-4 py-3">Referti</th>
                      <th className="px-4 py-3">Accesso</th>
                      <th className="px-4 py-3">Ruolo</th>
                      <th className="px-4 py-3 text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => {
                      const isAdmin = u.role === "admin";
                      const busy = busyId === u.user_id;
                      return (
                        <tr key={u.user_id} className="border-b border-slate-100 last:border-0" data-testid={`admin-user-row-${u.user_id}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-900">{u.email}</div>
                            <div className="text-xs text-slate-400">{u.name || "—"} · {u.auth_provider}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{u.struttura || "—"}</td>
                          <td className="px-4 py-3">
                            {u.unlocked ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Sbloccata
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500">
                                <XCircle className="w-3.5 h-3.5" /> Non pagata
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3" data-testid={`reports-cell-${u.user_id}`}>
                            <div className="font-medium text-slate-800">
                              {u.free_reports_used ?? 0} / {u.free_report_limit ?? 1}
                            </div>
                            <div className="text-xs text-slate-400">
                              {u.unlocked
                                ? `pagante · ${u.reports_generated ?? 0} referti`
                                : (u.free_reports_used ?? 0) === 0
                                  ? "mai usato"
                                  : (u.free_reports_used ?? 0) >= (u.free_report_limit ?? 1)
                                    ? "gratuito esaurito"
                                    : `${u.reports_generated ?? 0} referti`}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {u.active !== false ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-700">
                                <UserCheck className="w-3.5 h-3.5" /> Attivo
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                                <UserX className="w-3.5 h-3.5" /> Disattivato
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-semibold ${isAdmin ? "text-primary" : "text-slate-500"}`}>{u.role}</span>
                          </td>
                          <td className="px-4 py-3">
                            {isAdmin ? (
                              <span className="block text-right text-xs text-slate-300">—</span>
                            ) : (
                              <div className="flex items-center justify-end gap-2">
                                {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                                <Button size="sm" variant="outline" disabled={busy} data-testid={`free-credits-btn-${u.user_id}`}
                                  onClick={() => promptFreeCredits(u)}
                                  className="text-slate-700 border-slate-200 hover:bg-slate-50 hover:text-slate-700">
                                  <Gift className="w-3.5 h-3.5" /> Crediti
                                </Button>
                                {u.unlocked ? (
                                  <Button size="sm" variant="outline" disabled={busy} data-testid={`revoke-btn-${u.user_id}`}
                                    onClick={() => runAction(u.user_id, () => adminRevoke(u.user_id), "Licenza revocata.")}
                                    className="text-amber-700 border-amber-200 hover:bg-amber-50 hover:text-amber-700">
                                    <Ban className="w-3.5 h-3.5" /> Revoca
                                  </Button>
                                ) : (
                                  <Button size="sm" variant="outline" disabled={busy} data-testid={`unlock-btn-${u.user_id}`}
                                    onClick={() => runAction(u.user_id, () => adminUnlock(u.user_id), "Licenza sbloccata gratuitamente.")}
                                    className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700">
                                    <Unlock className="w-3.5 h-3.5" /> Sblocca
                                  </Button>
                                )}
                                {u.active !== false ? (
                                  <Button size="sm" variant="outline" disabled={busy} data-testid={`deactivate-btn-${u.user_id}`}
                                    onClick={() => runAction(u.user_id, () => adminSetActive(u.user_id, false), "Accesso disattivato.")}
                                    className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-700">
                                    <UserX className="w-3.5 h-3.5" /> Disattiva
                                  </Button>
                                ) : (
                                  <Button size="sm" variant="outline" disabled={busy} data-testid={`activate-btn-${u.user_id}`}
                                    onClick={() => runAction(u.user_id, () => adminSetActive(u.user_id, true), "Accesso riattivato.")}
                                    className="text-sky-700 border-sky-200 hover:bg-sky-50 hover:text-sky-700">
                                    <UserCheck className="w-3.5 h-3.5" /> Riattiva
                                  </Button>
                                )}
                                {/* Unica azione irreversibile del pannello: passa da un dialog
                                    con riconferma dell'email, non da un click singolo. */}
                                <Button size="sm" variant="outline" disabled={busy} data-testid={`delete-btn-${u.user_id}`}
                                  onClick={() => openDelete(u)}
                                  className="text-red-700 border-red-300 hover:bg-red-600 hover:text-white">
                                  <Trash2 className="w-3.5 h-3.5" /> Elimina
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) closeDelete(); }}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina definitivamente l'account</AlertDialogTitle>
            <AlertDialogDescription>
              L'operazione è irreversibile e non richiede all'utente alcuna conferma.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4">
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-900"
              data-testid="delete-user-email">
              {deleteTarget?.email}
            </p>
            {/* Dire cosa resta è tanto importante quanto dire cosa sparisce: chi elimina un
                account per una richiesta di cancellazione dati deve sapere che la traccia
                contabile del pagamento non viene toccata. */}
            <div className="grid gap-3 sm:grid-cols-2 text-sm">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Vengono eliminati</p>
                <ul className="mt-1.5 space-y-1 text-slate-600">
                  <li>• Account e credenziali</li>
                  <li>• Sessioni attive</li>
                  <li>• Licenza e configurazione</li>
                  <li>• Logo e firma depositati</li>
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Vengono conservati</p>
                <ul className="mt-1.5 space-y-1 text-slate-600">
                  <li>• Storico pagamenti Stripe</li>
                  <li>• Conteggi anti-abuso</li>
                </ul>
              </div>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="delete-confirm-email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Scrivi l'email per confermare
              </label>
              <Input id="delete-confirm-email" data-testid="delete-confirm-input" autoComplete="off"
                value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder={deleteTarget?.email || ""} />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction disabled={!canDelete} onClick={confirmDelete} data-testid="delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50">
              <Trash2 className="w-4 h-4" /> Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
